import {
  useQuery,
  useMutation,
  type MutationOptions,
  type SiteSessionTypeInput,
  type SiteSessionTypeUpdateInput,
} from '@hapana/shared/web/services/gqty'
import { useTranslation } from 'react-i18next'
import { useToast } from '@hapana/shared/web/components/shared/toast/toast.hooks'
import { TOAST_PROPS_OPTIONS } from '@hapana/shared/web/components/shared/toast/toast.constants'
import { useDialog } from '@hapana/shared/web/components/shared/dialog/dialog.hooks'
import {
  useAuditTracker,
  useAuditTrackerConfigOptional,
  AuditAction,
  AuditResource,
  AuditSeverity,
} from '@hapana/shared/web/hooks'

export const useSiteSessionTypes = (siteId?: string, search?: string) => {
  const query = useQuery({ suspense: false })

  const siteSessionTypes = siteId
    ? query.siteSessionTypes({ siteId, search: search || undefined })
    : null

  if (siteSessionTypes) {
    for (const sessionType of siteSessionTypes) {
      void sessionType.siteSessionTypeId
      void sessionType.name
      void sessionType.color
      void sessionType.allowMultiLocationAccess
      void sessionType.allowEditingSessionType
      void sessionType.isSystemManaged
      void sessionType.displayOrder
      if (sessionType.customColors) {
        for (const customColor of sessionType.customColors) {
          void customColor.colorName
          void customColor.hexCode
        }
      }
      void sessionType.createdAt
      void sessionType.updatedAt
      void sessionType.deletedAt
    }
  }

  return {
    data: siteSessionTypes || [],
    isLoading: query.$state.isLoading,
    error: query.$state.error,
    refetch: () => query.$refetch(),
  }
}

export const useSiteSessionType = (siteId?: string, id?: string) => {
  const query = useQuery({
    suspense: false,
    refetchOnRender: false,
  })

  const siteSessionType = siteId && id ? query.siteSessionType({ siteId, id }) : null

  if (siteSessionType) {
    void siteSessionType.siteSessionTypeId
    void siteSessionType.name
    void siteSessionType.color
    void siteSessionType.allowMultiLocationAccess
    void siteSessionType.allowEditingSessionType
    void siteSessionType.isSystemManaged
    if (siteSessionType.customColors) {
      for (const customColor of siteSessionType.customColors) {
        void customColor.colorName
        void customColor.hexCode
      }
    }
    void siteSessionType.createdAt
    void siteSessionType.updatedAt
    void siteSessionType.deletedAt
  }

  return {
    data: siteSessionType,
    isLoading: query.$state.isLoading,
    error: query.$state.error,
    refetch: () => query.$refetch(),
  }
}

export const useSiteSessionTypesByIds = (siteId?: string, siteSessionTypeIds?: string[]) => {
  const query = useQuery({
    suspense: false,
  })

  const siteSessionTypes =
    siteId && siteSessionTypeIds && siteSessionTypeIds.length > 0
      ? query.siteSessionTypesByIds({ siteId, siteSessionTypeIds })
      : null

  if (siteSessionTypes) {
    for (const sessionType of siteSessionTypes) {
      void sessionType.siteSessionTypeId
      void sessionType.name
      void sessionType.color
      void sessionType.allowMultiLocationAccess
      void sessionType.allowEditingSessionType
      void sessionType.isSystemManaged
      void sessionType.displayOrder
      if (sessionType.customColors) {
        for (const customColor of sessionType.customColors) {
          void customColor.colorName
          void customColor.hexCode
        }
      }
      void sessionType.createdAt
      void sessionType.updatedAt
      void sessionType.deletedAt
    }
  }

  return {
    data: siteSessionTypes || [],
    isLoading: query.$state.isLoading,
    error: query.$state.error,
    refetch: () => query.$refetch(),
  }
}

export const useCreateSiteSessionType = (
  options?: MutationOptions & { onRefetch?: () => void }
) => {
  const { openToast } = useToast()
  const { t } = useTranslation('core')
  const { closeDialog } = useDialog()
  const auditConfig = useAuditTrackerConfigOptional()
  const { createMutationCallbacks } = useAuditTracker({
    ...auditConfig,
    corporateId: auditConfig?.corporateId ?? '',
  })

  const [mutate, mutationState] = useMutation(
    (mutation, args: { siteId: string; input: SiteSessionTypeInput }) => {
      const { siteId, input } = args

      const result = mutation.createSiteSessionType({
        siteId,
        input,
      })

      if (result) {
        void result.siteSessionTypeId
        void result.name
        void result.color
        void result.displayOrder
        void result.allowMultiLocationAccess
        void result.allowEditingSessionType
        void result.isSystemManaged
        void result.customColors
        void result.createdAt
        void result.updatedAt
      }
      return result
    },
    {
      onComplete: (data) => {
        options?.onRefetch?.()

        const { onSuccess: auditOnSuccess } = createMutationCallbacks({
          action: AuditAction.SITE_SETTING_UPDATED,
          resource: AuditResource.SITE,
          resourceId: data?.siteSessionTypeId,
          changeType: 'create',
          severity: AuditSeverity.INFO,
          featureCategory: 'settings.scheduler.sessionTypes',
          settingName: 'sessionType',
          settingDisplayName: 'Session Type',
          getOldValue: () => null,
          getNewValue: () => ({
            name: data?.name,
            color: data?.color,
            allowMultiLocationAccess: data?.allowMultiLocationAccess,
            allowEditingSessionType: data?.allowEditingSessionType,
          }),
          getSummary: () => `Session type "${data?.name}" created`,
        })
        auditOnSuccess(data)

        openToast({
          title: t('settings.scheduler.sessionTypes.successMessages.created'),
          severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
        })
        options?.onSuccess?.(data)
        closeDialog()
      },
      onError: (error: Error) => {
        const { onError: auditOnError } = createMutationCallbacks({
          action: AuditAction.SITE_SETTING_UPDATED,
          resource: AuditResource.SITE,
          changeType: 'create',
          severity: AuditSeverity.INFO,
          featureCategory: 'settings.scheduler.sessionTypes',
          settingName: 'sessionType',
          settingDisplayName: 'Session Type',
        })
        auditOnError(error)

        openToast({
          title: t('settings.scheduler.sessionTypes.errorMessages.created'),
          severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
        })
        options?.onError?.(error)
      },
    }
  )

  return [mutate, mutationState]
}

export const useUpdateSiteSessionType = (
  options?: MutationOptions & { onRefetch?: () => void }
) => {
  const { openToast } = useToast()
  const { t } = useTranslation('core')
  const { closeDialog } = useDialog()
  const auditConfig = useAuditTrackerConfigOptional()
  const { createMutationCallbacks } = useAuditTracker({
    ...auditConfig,
    corporateId: auditConfig?.corporateId ?? '',
  })

  const [mutate, mutationState] = useMutation(
    (mutation, args: { siteId: string; id: string; input: SiteSessionTypeUpdateInput }) => {
      const { siteId, id, input } = args

      const result = mutation.updateSiteSessionType({
        siteId,
        id,
        input,
      })

      if (result) {
        void result.siteSessionTypeId
        void result.name
        void result.color
        void result.allowMultiLocationAccess
        void result.allowEditingSessionType
        void result.isSystemManaged
        void result.customColors
        void result.updatedAt
      }
      return result
    },
    {
      onComplete: (data) => {
        options?.onRefetch?.()

        const { onSuccess: auditOnSuccess } = createMutationCallbacks({
          action: AuditAction.SITE_SETTING_UPDATED,
          resource: AuditResource.SITE,
          resourceId: data?.siteSessionTypeId,
          changeType: 'update',
          severity: AuditSeverity.INFO,
          featureCategory: 'settings.scheduler.sessionTypes',
          settingName: 'sessionType',
          settingDisplayName: 'Session Type',
          getNewValue: () => ({
            name: data?.name,
            color: data?.color,
            allowMultiLocationAccess: data?.allowMultiLocationAccess,
            allowEditingSessionType: data?.allowEditingSessionType,
          }),
          getSummary: () => `Session type "${data?.name}" updated`,
        })
        auditOnSuccess(data)

        openToast({
          title: t('settings.scheduler.sessionTypes.successMessages.updated'),
          severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
        })
        options?.onSuccess?.(data)
        closeDialog()
      },
      onError: (error: Error) => {
        const { onError: auditOnError } = createMutationCallbacks({
          action: AuditAction.SITE_SETTING_UPDATED,
          resource: AuditResource.SITE,
          changeType: 'update',
          severity: AuditSeverity.INFO,
          featureCategory: 'settings.scheduler.sessionTypes',
          settingName: 'sessionType',
          settingDisplayName: 'Session Type',
        })
        auditOnError(error)

        openToast({
          title: t('settings.scheduler.sessionTypes.errorMessages.updated'),
          severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
        })
        options?.onError?.(error)
      },
    }
  )

  return [mutate, mutationState]
}

export const useDeleteSiteSessionType = (
  options?: MutationOptions & { onRefetch?: () => void }
) => {
  const { openToast } = useToast()
  const { t } = useTranslation('core')
  const { closeDialog } = useDialog()
  const auditConfig = useAuditTrackerConfigOptional()
  const { createMutationCallbacks } = useAuditTracker({
    ...auditConfig,
    corporateId: auditConfig?.corporateId ?? '',
  })

  const [mutate, mutationState] = useMutation(
    (mutation, args: { siteId: string; id: string }) => {
      const { siteId, id } = args

      const result = mutation.deleteSiteSessionType({
        siteId,
        id,
      })

      if (result) {
        void result.siteSessionTypeId
        void result.name
        void result.color
        void result.deletedAt
        void result.createdAt
        void result.updatedAt
      }

      return result
    },
    {
      onComplete: (data) => {
        options?.onRefetch?.()

        const { onSuccess: auditOnSuccess } = createMutationCallbacks({
          action: AuditAction.SITE_SETTING_UPDATED,
          resource: AuditResource.SITE,
          resourceId: data?.siteSessionTypeId,
          changeType: 'delete',
          severity: AuditSeverity.WARNING,
          featureCategory: 'settings.scheduler.sessionTypes',
          settingName: 'sessionType',
          settingDisplayName: 'Session Type',
          getOldValue: () => ({
            name: data?.name,
            color: data?.color,
          }),
          getNewValue: () => null,
          getSummary: () => `Session type "${data?.name}" deleted`,
        })
        auditOnSuccess(data)

        openToast({
          title: t('settings.scheduler.sessionTypes.successMessages.deleted'),
          severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
        })
        options?.onSuccess?.(data)
        closeDialog()
      },
      onError: (error: Error) => {
        const { onError: auditOnError } = createMutationCallbacks({
          action: AuditAction.SITE_SETTING_UPDATED,
          resource: AuditResource.SITE,
          changeType: 'delete',
          severity: AuditSeverity.WARNING,
          featureCategory: 'settings.scheduler.sessionTypes',
          settingName: 'sessionType',
          settingDisplayName: 'Session Type',
        })
        auditOnError(error)

        const errorDetails = (
          error as unknown as {
            graphQLErrors?: ReadonlyArray<{ extensions?: { details?: { errorCode?: string } } }>
          }
        ).graphQLErrors?.[0]?.extensions?.details
        const title =
          errorDetails?.errorCode === 'SESSION_TYPE_HAS_ASSOCIATIONS'
            ? t('settings.scheduler.sessionTypes.errorMessages.hasAssociations')
            : t('settings.scheduler.sessionTypes.errorMessages.deleted')
        openToast({
          title,
          severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
        })
        options?.onError?.(error)
      },
    }
  )

  return [mutate, mutationState]
}

export const useReorderSiteSessionTypes = (options?: { onRefetch?: () => void }) => {
  const { openToast } = useToast()
  const { t } = useTranslation('core')
  const auditConfig = useAuditTrackerConfigOptional()
  const { createMutationCallbacks } = useAuditTracker({
    ...auditConfig,
    corporateId: auditConfig?.corporateId ?? '',
  })

  const [mutate, mutationState] = useMutation(
    (
      mutation,
      args: {
        siteId: string
        input: { sessionTypes: Array<{ siteSessionTypeId: string; displayOrder: number }> }
      }
    ) => {
      const result = mutation.reorderSiteSessionTypes(args)
      void result.success
      return result
    },
    {
      onComplete: (data) => {
        options?.onRefetch?.()

        const { onSuccess: auditOnSuccess } = createMutationCallbacks({
          action: AuditAction.SITE_SETTING_UPDATED,
          resource: AuditResource.SITE,
          changeType: 'update',
          severity: AuditSeverity.INFO,
          featureCategory: 'settings.scheduler.sessionTypes',
          settingName: 'sessionType.displayOrder',
          settingDisplayName: 'Session Type Display Order',
          getNewValue: () => 'reordered',
          getSummary: () => 'Session types reordered',
        })
        auditOnSuccess(data)

        openToast({
          title: t('settings.scheduler.sessionTypes.successMessages.reordered'),
          severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
          horizontal: TOAST_PROPS_OPTIONS.HORIZONTAL.CENTER,
        })
      },
      onError: (error: Error) => {
        const { onError: auditOnError } = createMutationCallbacks({
          action: AuditAction.SITE_SETTING_UPDATED,
          resource: AuditResource.SITE,
          changeType: 'update',
          severity: AuditSeverity.INFO,
          featureCategory: 'settings.scheduler.sessionTypes',
          settingName: 'sessionType.displayOrder',
          settingDisplayName: 'Session Type Display Order',
        })
        auditOnError(error)

        options?.onRefetch?.()
        openToast({
          title: t('settings.scheduler.sessionTypes.errorMessages.reordered'),
          severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
          horizontal: TOAST_PROPS_OPTIONS.HORIZONTAL.CENTER,
        })
      },
    }
  )

  return [mutate, mutationState] as const
}
